import useAppView from 'Core/Hooks/useAppView'
import useMultiSlice from 'Core/Hooks/useMultiSlice'
import { SearchInput } from 'V2Components'
import { KindToQueryIndexMap, findActivityLogById, useMultiQuery } from 'features/activityLogs'
import { createRsrcKey } from 'features/resources'
import _ from 'lodash'
import moment from 'moment'
import { LoadingModal, ResponsiveTable, Typography } from 'procyon-ui'
import React, { useEffect, useState } from 'react'
import { ActivityLogInfoModal } from '../AcitivityLogInfoModal'
import { columns, useActivityLogs } from './useActivityLogs.utils'
import { Filters } from './components/Filters'

const ActivityLogs = ({ userName = '', rsrc = null }) => {
  const [searchKey, setSearchKey] = useState('')
  const [showActivityLogInfoModal, setShowActivityLogInfoModal] = useState(false)
  const [selectedActivityLog, setSelectedActivityLog] = useState(null)

  const [filters, setFilters] = useState({
    startDateTime: moment().subtract(7, 'days'),
    endDateTime: moment(),
    selectedUsers: [],
    resourceKindFilter: 'all',
    queryIndexes: ['event-log', 'audit-log', 'proxy-event-log', 'proxy-audit-log']
  })

  const { slices, dispatchThunks } = useMultiSlice(['userList'])
  const { appView } = useAppView()

  const { getQueryLogs, hits, fetching, backgroundFetching } = useMultiQuery({
    indexes:
      rsrc && KindToQueryIndexMap[rsrc.ObjectMeta.Kind]
        ? KindToQueryIndexMap[rsrc.ObjectMeta.Kind]
        : filters.queryIndexes,
    endDateTime: filters.endDateTime.toDate(),
    startDateTime: filters.startDateTime.toDate(),
    userName: userName ? userName : filters.selectedUsers.length > 0 ? filters.selectedUsers[0].ObjectMeta.Name : ''
  })

  const { getRowData } = useActivityLogs({ hits, searchKey, usersList: slices.userList })

  /**
   * The resource based filter on the logs are done at row data level
   * @returns {any[]}
   */
  const getFilteredRowData = () => {
    const tableData = getRowData()

    if (!rsrc) {
      //Filter based on Kind
      return tableData.filter((r) => {
        const { resourceKind } = r
        if (filters.resourceKindFilter !== 'all' && resourceKind !== filters.resourceKindFilter)
          return false
        return true
      })
    }

    const rsrcKey = createRsrcKey(rsrc)
    const rsrcName = rsrc.ObjectMeta.Name

    return tableData.filter((r) => {
      const { resources } = r
      if (!resources.length) return
      const rs = resources[0]
      if (!rs) return
      // If the rsrc key of the rsrc matches with the resource of the log then, show row
      // or, if the name on the log matches the name of the resource then show the row
      if (rs.key === rsrcKey || rs.Name === rsrcName) return true
    })
  }

  const handleFilterChange = (v = {}) => {
    if ('resourceKindFilter' in v) {
      const nextResourceKind = v.resourceKindFilter
      const nextQueryIndexes =
        nextResourceKind === 'all'
          ? KindToQueryIndexMap._ALL
          : KindToQueryIndexMap[nextResourceKind]

      setFilters((s) => ({ ...s, ...v, queryIndexes: nextQueryIndexes }))
    } else {
      setFilters((s) => ({ ...s, ...v }))
    }
  }

  const handleApplyClick = () => {
    getQueryLogs()
  }

  useEffect(() => {
    dispatchThunks()
    getQueryLogs()
  }, [])

  const tableData = getFilteredRowData()

  return (
    <div>
      <div className='flex justify-between items-center'>
        <div className='flex items-center gap-4'>
          <Typography variant='h3'>Activity Logs</Typography>
        </div>
        <SearchInput sx={{ width: '280px' }} searchKey={searchKey} onChange={setSearchKey} />
      </div>

      <div className='bg-[var(--color-common-bg-base)] p-4 mt-8'>
        <Filters
          filters={filters}
          onFilterChange={handleFilterChange}
          onApplyClick={handleApplyClick}
          rsrc={rsrc}
          userName={userName}
        />

        <div className='mt-6'>
          {fetching ? (
            <div className='h-[400px] flex justify-center items-center'>
              <LoadingModal message='Fetching Logs' shadow={false} />
            </div>
          ) : (
            <ResponsiveTable
              scrollX='1200px'
              onRow={(data) => {
                return {
                  onClick() {
                    const hit = findActivityLogById(hits, data.id)
                    if (!hit) return
                    setSelectedActivityLog(hit)
                    setShowActivityLogInfoModal(true)
                  }
                }
              }}
              columns={columns()}
              data={tableData}
            />
          )}
        </div>
      </div>

      {showActivityLogInfoModal && selectedActivityLog && (
        <ActivityLogInfoModal
          onClose={() => {
            setSelectedActivityLog(null)
            setShowActivityLogInfoModal(false)
          }}
          hit={selectedActivityLog}
        />
      )}
    </div>
  )
}

export { ActivityLogs }
